import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useGlobalChat } from './GlobalAIChatProvider';
import { useAgent } from './AgentProvider';
import ConversationHistory from './ConversationHistory';
import AgentAnalytics from './AgentAnalytics';
import ConversationViewer from './ConversationViewer';
import SettingsPanel from './SettingsPanel';
import {
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  ClockIcon,
  Cog6ToothIcon,
  PlusIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

/**
 * AssistantDashboard - Dedicated dashboard for AI assistant
 *
 * Route: /assistant
 *
 * Features:
 * - Conversation history with search
 * - Agent usage analytics
 * - Conversation viewer/replay
 * - Settings and preferences
 * - Quick actions (new conversation, export)
 */
const AssistantDashboard = ({ user, company, georgeCoachEnabled = false }) => {
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState('conversations');
  const [selectedConversation, setSelectedConversation] = useState(null);

  const {
    conversations,
    createConversation,
    deleteConversation,
    openChat
  } = useGlobalChat();

  const { activeAgentType } = useAgent();

  // Tab configuration
  const tabs = [
    {
      id: 'conversations',
      name: intl.formatMessage({ id: 'succession.agent_system.assistant_dashboard.tab_conversations' }),
      icon: ChatBubbleLeftRightIcon,
      count: conversations.length
    },
    {
      id: 'analytics',
      name: intl.formatMessage({ id: 'succession.agent_system.assistant_dashboard.tab_analytics' }),
      icon: ChartBarIcon,
      count: null
    },
    {
      id: 'history',
      name: intl.formatMessage({ id: 'succession.agent_system.assistant_dashboard.tab_history' }),
      icon: ClockIcon,
      count: null
    },
    {
      id: 'settings',
      name: intl.formatMessage({ id: 'succession.agent_system.assistant_dashboard.tab_settings' }),
      icon: Cog6ToothIcon,
      count: null
    }
  ];

  const handleNewConversation = async () => {
    const result = await createConversation('succession_dashboard_agent');
    if (result?.conversationId) {
      openChat();
    }
  };

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    setActiveTab('history');
  };

  return (
    <div className="min-h-screen bg-succession-bg-primary">
      {/* Header */}
      <div className="bg-white border-b border-succession-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              {/* Title */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-succession-primary to-succession-secondary rounded-xl flex items-center justify-center shadow-lg">
                  <SparklesIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-succession-text-primary">
                    {intl.formatMessage({ id: georgeCoachEnabled ? 'succession.agent_system.assistant_dashboard.george_title' : 'succession.agent_system.assistant_dashboard.title' })}
                  </h1>
                  <p className="text-sm text-succession-text-secondary mt-1">
                    {intl.formatMessage({ id: georgeCoachEnabled ? 'succession.agent_system.assistant_dashboard.george_subtitle' : 'succession.agent_system.assistant_dashboard.subtitle' })}
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-3">
                {activeAgentType && (
                  <span className="px-3 py-1.5 bg-succession-primary bg-opacity-10 text-succession-primary text-sm font-medium rounded-full border border-succession-primary">
                    {intl.formatMessage({ id: 'succession.agent_system.assistant_dashboard.active_agent' }, { agentType: activeAgentType.replace(/_/g, ' ').replace(/agent$/, '').trim() })}
                  </span>
                )}
                <button
                  onClick={handleNewConversation}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-succession-primary text-white rounded-lg hover:bg-succession-primary-hover transition-colors shadow-sm"
                >
                  <PlusIcon className="h-5 w-5" />
                  {intl.formatMessage({ id: 'succession.agent_system.assistant_dashboard.new_conversation' })}
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="mt-6 border-b border-succession-border">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;

                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        group inline-flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                        ${isActive
                          ? 'border-succession-primary text-succession-primary'
                          : 'border-transparent text-succession-text-secondary hover:text-succession-text-primary hover:border-succession-border'
                        }
                      `}
                    >
                      <Icon className="h-5 w-5" />
                      <span>{tab.name}</span>
                      {tab.count !== null && (
                        <span className={`
                          ml-2 py-0.5 px-2 rounded-full text-xs font-medium
                          ${isActive
                            ? 'bg-succession-primary text-white'
                            : 'bg-succession-bg-secondary text-succession-text-secondary'
                          }
                        `}>
                          {tab.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'conversations' && (
          <ConversationHistory
            conversations={conversations}
            onSelect={handleConversationSelect}
            onDelete={deleteConversation}
            onOpenChat={openChat}
          />
        )}

        {activeTab === 'analytics' && (
          <AgentAnalytics
            conversations={conversations}
            user={user}
          />
        )}

        {activeTab === 'history' && (
          <ConversationViewer
            conversation={selectedConversation}
            onBack={() => setSelectedConversation(null)}
          />
        )}

        {activeTab === 'settings' && (
          <SettingsPanel
            user={user}
            company={company}
          />
        )}
      </div>
    </div>
  );
};

AssistantDashboard.propTypes = {
  user: PropTypes.object,
  company: PropTypes.object
};

export default AssistantDashboard;
